import { useTranslation } from 'react-i18next'

import { ErrorState } from '@/components/feedback/ErrorState'
import { Skeleton } from '@/components/feedback/Skeleton'
import { Sheet } from '@/components/ui/Sheet'
import { useInvoicePreview } from '@/features/finance/api/useInvoices'

/* The invoice as the page it will print.
 *
 * ── Why this is a frame and not markup ──────────────────────────────────────
 *
 * What the server returns is a COMPLETE HTML document composed by
 * platform/docs — the same template the archived PDF comes off, letterhead,
 * fonts and all. Two things follow, and both are load-bearing:
 *
 *   · It is not this application's markup and must never join this
 *     application's DOM. Its styles would leak into the page around it, and its
 *     content is document text rather than UI this codebase wrote.
 *   · It is shown in a SANDBOXED frame with no permissions at all — no scripts,
 *     no forms, no same-origin. The document is composed from a client's own
 *     name, address and charge descriptions, which is to say from text that
 *     people type; `srcDoc` with an empty `sandbox` is what makes it
 *     unambiguously inert. Nothing in the template needs a script to render.
 *
 * ── Why it replaces the sketch that used to sit here ────────────────────────
 *
 * The screen previously drew its own approximation of the document beside the
 * editor: a header, the lines, a total, laid out in this app's own components
 * and captioned "this is a rendering of what has been typed, not the document
 * itself". An approximation of a document is a document nobody has checked —
 * the only place the two could be compared was the client's inbox. This shows
 * the real page, so a difference between the screen and the paper is no longer
 * possible to have.
 */
export function InvoicePreviewSheet({
  open,
  onClose,
  caseId,
  invoiceId,
}: {
  open: boolean
  onClose: () => void
  caseId: string
  invoiceId: string
}) {
  const { t } = useTranslation()
  // Asked for only while the sheet is open, so a table of twenty invoices
  // renders twenty rows and composes no documents.
  const preview = useInvoicePreview(caseId, invoiceId, open)

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={t('finance.documents.preview.title')}
      size="wide"
    >
      <p className="text-text-muted text-[12.5px] leading-snug font-light">
        {t('finance.documents.preview.subtitle')}
      </p>

      {preview.isPending ? (
        <Skeleton className="h-[70vh] rounded-[var(--radius-DEFAULT)]" />
      ) : preview.isError ? (
        <ErrorState error={preview.error} onRetry={() => void preview.refetch()} />
      ) : (
        <div className="border-line overflow-hidden rounded-[var(--radius-DEFAULT)] border bg-white">
          <iframe
            // The document names itself for assistive technology; without a
            // title a frame is announced as "frame", which tells a screen
            // reader user nothing about what is inside it.
            title={t('finance.documents.preview.title')}
            srcDoc={preview.data}
            // Empty sandbox: every permission withheld. See the note above.
            sandbox=""
            className="h-[70vh] w-full border-0 bg-white"
          />
        </div>
      )}
    </Sheet>
  )
}
